import React, { useState, useEffect, useCallback } from 'react';
import * as XLSX from 'xlsx';

const API = 'https://api.botanix.in/api/kitchen';

const C = {
  accent:'#3AAF58', accentLight:'#EAF7EE',
  bg:'#F3F6F3', surface:'#FFFFFF',
  border:'#E2E8E2', border2:'#CDD8CD',
  text:'#1A2E1E', text2:'#3D5E44', text3:'#7A9A80',
  amber:'#D4840F', amberBg:'#FEF6E7', amberBorder:'#F0D499',
  red:'#C83333', redBg:'#FDEAEA', redBorder:'#F0B8B8',
  blue:'#2563A8', blueBg:'#EAF1FB',
};

const CUISINES = ['Indian','Chinese','Continental','Italian','Mexican','Thai','Japanese','Mediterranean','American','Coffee Shop','Bakery','Beverages','Other'];
const CATEGORIES = ['Starter','Soup','Salad','Main Course','Bread','Rice & Biryani','Dessert','Beverages','Buffet','Snacks','Breakfast','Street Food','Other'];
const SPICE_LEVELS = ['Mild','Medium','Hot','Extra Hot','No Spice'];
const MEAL_TIMES = ['All','Breakfast','Lunch','Dinner','High Tea','All Day'];

// Calorie reference database for auto-suggest
const CALORIE_DB = {
  'dal makhani': { calories: 180, protein: 8, carbs: 22, fat: 7 },
  'butter chicken': { calories: 290, protein: 25, carbs: 12, fat: 16 },
  'paneer butter masala': { calories: 260, protein: 12, carbs: 18, fat: 16 },
  'biryani': { calories: 350, protein: 18, carbs: 45, fat: 10 },
  'naan': { calories: 260, protein: 8, carbs: 45, fat: 5 },
  'roti': { calories: 120, protein: 3, carbs: 24, fat: 2 },
  'rice': { calories: 130, protein: 3, carbs: 28, fat: 0 },
  'dosa': { calories: 168, protein: 4, carbs: 32, fat: 3 },
  'idli': { calories: 58, protein: 2, carbs: 12, fat: 0 },
  'samosa': { calories: 260, protein: 5, carbs: 30, fat: 13 },
  'gulab jamun': { calories: 180, protein: 3, carbs: 32, fat: 5 },
  'rasgulla': { calories: 106, protein: 3, carbs: 20, fat: 2 },
  'coffee': { calories: 35, protein: 1, carbs: 6, fat: 1 },
  'tea': { calories: 15, protein: 0, carbs: 3, fat: 0 },
  'lassi': { calories: 150, protein: 6, carbs: 22, fat: 4 },
  'soup': { calories: 80, protein: 4, carbs: 12, fat: 2 },
  'salad': { calories: 60, protein: 3, carbs: 10, fat: 1 },
  'sandwich': { calories: 280, protein: 12, carbs: 36, fat: 8 },
  'pizza': { calories: 266, protein: 11, carbs: 33, fat: 10 },
  'pasta': { calories: 220, protein: 8, carbs: 40, fat: 3 },
  'tandoori chicken': { calories: 165, protein: 25, carbs: 5, fat: 5 },
  'fish': { calories: 140, protein: 20, carbs: 0, fat: 6 },
  'prawn': { calories: 120, protein: 18, carbs: 2, fat: 4 },
  'mutton': { calories: 250, protein: 22, carbs: 0, fat: 17 },
};

function getCalorieSuggestion(name) {
  if (!name) return null;
  const lower = name.toLowerCase();
  for (const [key, val] of Object.entries(CALORIE_DB)) {
    if (lower.includes(key)) return val;
  }
  return null;
}

const initForm = {
  item_name: '', category: 'Main Course', cuisine: 'Indian',
  description: '', selling_price: '', cost_price: '',
  gst_rate: 5, calories: '', protein: '', carbs: '', fat: '',
  is_veg: true, is_vegan: false, allergens: '',
  prep_time: 15, spice_level: 'Medium', available_for: 'All',
  active_status: true,
};

export default function FoodMenu() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('menu');
  const [search, setSearch] = useState('');
  const [filterCuisine, setFilterCuisine] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterVeg, setFilterVeg] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(initForm);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [importData, setImportData] = useState(null);
  const [importing, setImporting] = useState(false);
  const [calSuggestion, setCalSuggestion] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/menu`);
      const d = await r.json();
      setItems(Array.isArray(d) ? d : []);
    } catch(e) {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => {
    setForm(p => ({ ...p, [k]: v }));
    if (k === 'item_name') {
      const sug = getCalorieSuggestion(v);
      setCalSuggestion(sug);
    }
  };

  const applySuggestion = () => {
    if (!calSuggestion) return;
    setForm(p => ({ ...p, calories: calSuggestion.calories, protein: calSuggestion.protein, carbs: calSuggestion.carbs, fat: calSuggestion.fat }));
    setCalSuggestion(null);
  };

  const showMsg = (m) => { setMsg(m); setTimeout(() => setMsg(''), 3000); };

  const saveItem = async () => {
    if (!form.item_name || !form.selling_price) { showMsg('Name and price are required'); return; }
    setSaving(true);
    try {
      const url = editItem ? `${API}/menu/${editItem.id}` : `${API}/menu`;
      const method = editItem ? 'PUT' : 'POST';
      const r = await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
      if (r.ok) { setShowAdd(false); setEditItem(null); setForm(initForm); load(); showMsg(editItem ? 'Item updated!' : 'Item added!'); }
      else { const d = await r.json(); showMsg(d.error || 'Failed'); }
    } catch(e) { showMsg('Error saving'); }
    setSaving(false);
  };

  const deleteItem = async (id) => {
    if (!window.confirm('Delete this menu item?')) return;
    await fetch(`${API}/menu/${id}`, { method: 'DELETE' });
    load(); showMsg('Item deleted');
  };

  const toggleActive = async (item) => {
    await fetch(`${API}/menu/${item.id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...item, active_status: !item.active_status })
    });
    load();
  };

  const openEdit = (item) => {
    setForm({ ...initForm, ...item });
    setEditItem(item);
    setShowAdd(true);
  };

  // Excel import
  const handleExcelUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const wb = XLSX.read(ev.target.result, { type: 'binary' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const data = XLSX.utils.sheet_to_json(ws);
      setImportData(data);
    };
    reader.readAsBinaryString(file);
  };

  const executeImport = async () => {
    if (!importData?.length) return;
    setImporting(true);
    try {
      const items = importData.map(row => ({
        item_name: row['Item Name'] || row['item_name'] || row['Name'],
        category: row['Category'] || row['category'] || 'Main Course',
        cuisine: row['Cuisine'] || row['cuisine'] || 'Indian',
        description: row['Description'] || row['description'] || '',
        selling_price: parseFloat(row['Price'] || row['selling_price'] || row['Selling Price'] || 0),
        cost_price: parseFloat(row['Cost Price'] || row['cost_price'] || 0) || null,
        gst_rate: parseFloat(row['GST Rate'] || row['gst_rate'] || 5),
        calories: parseInt(row['Calories'] || row['calories'] || 0) || null,
        is_veg: row['Veg'] === 'Yes' || row['is_veg'] === true || row['Type'] === 'Veg',
        spice_level: row['Spice Level'] || row['spice_level'] || 'Medium',
        prep_time: parseInt(row['Prep Time'] || row['prep_time'] || 15),
      }));
      const r = await fetch(`${API}/menu/bulk-import`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });
      const d = await r.json();
      if (r.ok) { showMsg(`Imported ${d.imported} of ${d.total} items!`); setImportData(null); load(); setActiveTab('menu'); }
      else showMsg(d.error || 'Import failed');
    } catch(e) { showMsg('Import error'); }
    setImporting(false);
  };

  const downloadTemplate = () => {
    const template = [
      { 'Item Name': 'Dal Makhani', 'Category': 'Main Course', 'Cuisine': 'Indian', 'Description': 'Creamy black lentils', 'Price': 280, 'Cost Price': 80, 'GST Rate': 5, 'Calories': 180, 'Veg': 'Yes', 'Spice Level': 'Medium', 'Prep Time': 20 },
      { 'Item Name': 'Butter Chicken', 'Category': 'Main Course', 'Cuisine': 'Indian', 'Description': 'Tender chicken in rich tomato gravy', 'Price': 320, 'Cost Price': 100, 'GST Rate': 5, 'Calories': 290, 'Veg': 'No', 'Spice Level': 'Medium', 'Prep Time': 25 },
    ];
    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Menu Template');
    XLSX.writeFile(wb, 'botanix_menu_template.xlsx');
  };

  const filtered = items.filter(i => {
    const matchSearch = !search || i.item_name?.toLowerCase().includes(search.toLowerCase());
    const matchCuisine = !filterCuisine || i.cuisine === filterCuisine;
    const matchCat = !filterCategory || i.category === filterCategory;
    const matchVeg = !filterVeg || (filterVeg === 'veg' ? i.is_veg : !i.is_veg);
    return matchSearch && matchCuisine && matchCat && matchVeg;
  });

  const byCategory = filtered.reduce((acc, item) => {
    const cat = item.category || 'Other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});

  const inp = {
    width: '100%', padding: '9px 12px', background: C.bg,
    border: `1.5px solid ${C.border2}`, borderRadius: 8,
    color: C.text, fontSize: 13, boxSizing: 'border-box',
  };
  const lbl = (t) => <div style={{ fontSize: 11, fontWeight: 700, color: C.text3, textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 5 }}>{t}</div>;

  const FormModal = () => (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, overflowY: 'auto' }}>
      <div style={{ background: C.surface, borderRadius: 16, width: '100%', maxWidth: 640, maxHeight: '90vh', overflowY: 'auto', padding: 28, boxShadow: '0 20px 60px rgba(0,0,0,.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ fontSize: 17, fontWeight: 800, color: C.text }}>{editItem ? 'Edit Menu Item' : 'Add Menu Item'}</div>
          <button onClick={() => { setShowAdd(false); setEditItem(null); setForm(initForm); }} style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: '6px 12px', cursor: 'pointer', color: C.text2 }}>Cancel</button>
        </div>

        {/* Basic Info */}
        <div style={{ marginBottom: 14 }}>
          {lbl('Item Name *')}
          <input style={inp} value={form.item_name} onChange={e => set('item_name', e.target.value)} placeholder="e.g. Dal Makhani" />
          {calSuggestion && (
            <div style={{ marginTop: 6, padding: '8px 12px', background: C.blueBg, borderRadius: 8, fontSize: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: C.blue }}>Auto-detected: {calSuggestion.calories} kcal | P:{calSuggestion.protein}g | C:{calSuggestion.carbs}g | F:{calSuggestion.fat}g</span>
              <button onClick={applySuggestion} style={{ padding: '3px 10px', background: C.blue, color: '#fff', border: 'none', borderRadius: 6, fontSize: 11, cursor: 'pointer', fontWeight: 700 }}>Apply</button>
            </div>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
          <div>
            {lbl('Category')}
            <select style={inp} value={form.category} onChange={e => set('category', e.target.value)}>
              {CATEGORIES.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div>
            {lbl('Cuisine')}
            <select style={inp} value={form.cuisine} onChange={e => set('cuisine', e.target.value)}>
              {CUISINES.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
        </div>

        <div style={{ marginBottom: 14 }}>
          {lbl('Description')}
          <textarea style={{ ...inp, height: 60, resize: 'vertical' }} value={form.description} onChange={e => set('description', e.target.value)} placeholder="Brief description of the dish..." />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 14 }}>
          <div>{lbl('Selling Price (Rs) *')}<input style={inp} type="number" value={form.selling_price} onChange={e => set('selling_price', e.target.value)} /></div>
          <div>{lbl('Cost Price (Rs)')}<input style={inp} type="number" value={form.cost_price} onChange={e => set('cost_price', e.target.value)} /></div>
          <div>{lbl('GST Rate %')}<input style={inp} type="number" value={form.gst_rate} onChange={e => set('gst_rate', e.target.value)} /></div>
        </div>

        {/* Nutrition */}
        <div style={{ padding: '12px 14px', background: C.bg, borderRadius: 10, marginBottom: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.text2, marginBottom: 10 }}>Nutrition Info (per serving)</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10 }}>
            <div>{lbl('Calories (kcal)')}<input style={inp} type="number" value={form.calories} onChange={e => set('calories', e.target.value)} placeholder="e.g. 180" /></div>
            <div>{lbl('Protein (g)')}<input style={inp} type="number" value={form.protein} onChange={e => set('protein', e.target.value)} /></div>
            <div>{lbl('Carbs (g)')}<input style={inp} type="number" value={form.carbs} onChange={e => set('carbs', e.target.value)} /></div>
            <div>{lbl('Fat (g)')}<input style={inp} type="number" value={form.fat} onChange={e => set('fat', e.target.value)} /></div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 14 }}>
          <div>
            {lbl('Spice Level')}
            <select style={inp} value={form.spice_level} onChange={e => set('spice_level', e.target.value)}>
              {SPICE_LEVELS.map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div>
            {lbl('Available For')}
            <select style={inp} value={form.available_for} onChange={e => set('available_for', e.target.value)}>
              {MEAL_TIMES.map(m => <option key={m}>{m}</option>)}
            </select>
          </div>
          <div>{lbl('Prep Time (mins)')}<input style={inp} type="number" value={form.prep_time} onChange={e => set('prep_time', e.target.value)} /></div>
        </div>

        <div style={{ marginBottom: 14 }}>
          {lbl('Allergens')}
          <input style={inp} value={form.allergens} onChange={e => set('allergens', e.target.value)} placeholder="e.g. Gluten, Dairy, Nuts" />
        </div>

        <div style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input type="checkbox" checked={form.is_veg} onChange={e => set('is_veg', e.target.checked)} />
            <span style={{ fontSize: 13, color: C.accent, fontWeight: 600 }}>Vegetarian</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input type="checkbox" checked={form.is_vegan} onChange={e => set('is_vegan', e.target.checked)} />
            <span style={{ fontSize: 13, color: C.text2, fontWeight: 600 }}>Vegan</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input type="checkbox" checked={form.active_status} onChange={e => set('active_status', e.target.checked)} />
            <span style={{ fontSize: 13, color: C.text2, fontWeight: 600 }}>Active</span>
          </label>
        </div>

        <button onClick={saveItem} disabled={saving}
          style={{ width: '100%', padding: '13px', background: C.accent, color: '#fff', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
          {saving ? 'Saving...' : editItem ? 'Update Item' : 'Add to Menu'}
        </button>
      </div>
    </div>
  );

  return (
    <div style={{ padding: '24px 28px', background: C.bg, minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, color: C.text }}>Food Menu Management</div>
          <div style={{ fontSize: 13, color: C.text3, marginTop: 4 }}>{items.length} items · {items.filter(i => i.active_status).length} active</div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={() => setActiveTab('import')}
            style={{ padding: '9px 16px', background: C.blueBg, color: C.blue, border: `1px solid ${C.blue}`, borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
            Import Excel
          </button>
          <button onClick={() => { setShowAdd(true); setEditItem(null); setForm(initForm); }}
            style={{ padding: '9px 18px', background: C.accent, color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
            + Add Item
          </button>
        </div>
      </div>

      {/* Message */}
      {msg && <div style={{ padding: '10px 16px', borderRadius: 10, marginBottom: 16, fontSize: 13, fontWeight: 600, background: msg.includes('error') || msg.includes('Failed') ? C.redBg : C.accentLight, color: msg.includes('error') || msg.includes('Failed') ? C.red : C.accent, border: `1px solid ${msg.includes('error') || msg.includes('Failed') ? C.red : C.accent}` }}>{msg}</div>}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {[['menu', 'Menu Items'], ['import', 'Import / Export']].map(([id, label]) => (
          <button key={id} onClick={() => setActiveTab(id)}
            style={{ padding: '8px 16px', background: activeTab === id ? C.accent : C.surface, color: activeTab === id ? '#fff' : C.text2, border: `1px solid ${activeTab === id ? C.accent : C.border}`, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            {label}
          </button>
        ))}
      </div>

      {/* MENU TAB */}
      {activeTab === 'menu' && (
        <>
          {/* Filters */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search menu items..."
              style={{ padding: '8px 14px', background: C.surface, border: `1px solid ${C.border2}`, borderRadius: 8, color: C.text, fontSize: 13, width: 220 }} />
            <select value={filterCuisine} onChange={e => setFilterCuisine(e.target.value)}
              style={{ padding: '8px 12px', background: C.surface, border: `1px solid ${C.border2}`, borderRadius: 8, color: C.text, fontSize: 13 }}>
              <option value="">All Cuisines</option>
              {CUISINES.map(c => <option key={c}>{c}</option>)}
            </select>
            <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)}
              style={{ padding: '8px 12px', background: C.surface, border: `1px solid ${C.border2}`, borderRadius: 8, color: C.text, fontSize: 13 }}>
              <option value="">All Categories</option>
              {CATEGORIES.map(c => <option key={c}>{c}</option>)}
            </select>
            <select value={filterVeg} onChange={e => setFilterVeg(e.target.value)}
              style={{ padding: '8px 12px', background: C.surface, border: `1px solid ${C.border2}`, borderRadius: 8, color: C.text, fontSize: 13 }}>
              <option value="">All Items</option>
              <option value="veg">Veg Only</option>
              <option value="nonveg">Non-Veg Only</option>
            </select>
            <button onClick={load} style={{ padding: '8px 14px', background: C.surface, border: `1px solid ${C.border2}`, borderRadius: 8, color: C.text2, fontSize: 13, cursor: 'pointer' }}>Refresh</button>
          </div>

          {loading ? <div style={{ textAlign: 'center', padding: 60, color: C.text3 }}>Loading menu...</div> : (
            Object.entries(byCategory).length === 0 ? (
              <div style={{ textAlign: 'center', padding: 60, background: C.surface, borderRadius: 12, color: C.text3 }}>
                No menu items found. Click "Add Item" to get started.
              </div>
            ) : (
              Object.entries(byCategory).map(([category, catItems]) => (
                <div key={category} style={{ marginBottom: 24 }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: C.text2, textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 10, paddingBottom: 6, borderBottom: `2px solid ${C.accent}`, display: 'inline-block' }}>
                    {category} ({catItems.length})
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(320px,1fr))', gap: 12 }}>
                    {catItems.map(item => (
                      <div key={item.id} style={{ background: C.surface, border: `1px solid ${item.active_status ? C.border : C.border2}`, borderRadius: 12, padding: 16, opacity: item.active_status ? 1 : 0.6, borderLeft: `4px solid ${item.is_veg ? C.accent : C.red}` }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                              <div style={{ width: 10, height: 10, borderRadius: '50%', background: item.is_veg ? C.accent : C.red, border: `2px solid ${item.is_veg ? C.accent : C.red}`, flexShrink: 0 }} />
                              <div style={{ fontSize: 14, fontWeight: 800, color: C.text }}>{item.item_name}</div>
                            </div>
                            <div style={{ fontSize: 11, color: C.text3 }}>{item.cuisine} · {item.spice_level || 'Medium'} · {item.prep_time || 15} mins</div>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: 16, fontWeight: 800, color: C.accent }}>Rs.{item.selling_price}</div>
                            {item.cost_price && <div style={{ fontSize: 11, color: C.text3 }}>Cost: Rs.{item.cost_price}</div>}
                          </div>
                        </div>

                        {item.description && <div style={{ fontSize: 12, color: C.text2, marginBottom: 8, lineHeight: 1.5 }}>{item.description}</div>}

                        {item.calories && (
                          <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 6, background: C.amberBg, color: C.amber, fontWeight: 600 }}>{item.calories} kcal</span>
                            {item.protein && <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 6, background: C.accentLight, color: C.accent, fontWeight: 600 }}>P:{item.protein}g</span>}
                            {item.carbs && <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 6, background: C.blueBg, color: C.blue, fontWeight: 600 }}>C:{item.carbs}g</span>}
                            {item.fat && <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 6, background: C.redBg, color: C.red, fontWeight: 600 }}>F:{item.fat}g</span>}
                          </div>
                        )}

                        {item.allergens && <div style={{ fontSize: 11, color: C.amber, marginBottom: 8 }}>Allergens: {item.allergens}</div>}

                        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                          <button onClick={() => openEdit(item)} style={{ flex: 1, padding: '6px', background: C.accentLight, color: C.accent, border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Edit</button>
                          <button onClick={() => toggleActive(item)} style={{ flex: 1, padding: '6px', background: C.bg, color: C.text2, border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>{item.active_status ? 'Deactivate' : 'Activate'}</button>
                          <button onClick={() => deleteItem(item.id)} style={{ padding: '6px 10px', background: C.redBg, color: C.red, border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Del</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )
          )}
        </>
      )}

      {/* IMPORT TAB */}
      {activeTab === 'import' && (
        <div style={{ maxWidth: 640 }}>
          <div style={{ background: C.surface, borderRadius: 12, padding: 24, marginBottom: 16, border: `1px solid ${C.border}` }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: C.text, marginBottom: 8 }}>Import Menu from Excel</div>
            <div style={{ fontSize: 13, color: C.text2, marginBottom: 16, lineHeight: 1.6 }}>
              Upload an Excel file with your menu items. Download the template to see the required format.
            </div>
            <button onClick={downloadTemplate}
              style={{ padding: '8px 16px', background: C.blueBg, color: C.blue, border: `1px solid ${C.blue}`, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', marginBottom: 16 }}>
              Download Template
            </button>
            <div style={{ marginBottom: 16 }}>
              <input type="file" accept=".xlsx,.xls,.csv" onChange={handleExcelUpload}
                style={{ display: 'block', padding: '10px', background: C.bg, border: `2px dashed ${C.border2}`, borderRadius: 8, width: '100%', color: C.text, cursor: 'pointer' }} />
            </div>
            {importData && (
              <div>
                <div style={{ padding: '10px 14px', background: C.accentLight, borderRadius: 8, marginBottom: 12, fontSize: 13, color: C.accent, fontWeight: 600 }}>
                  {importData.length} items ready to import
                </div>
                <div style={{ maxHeight: 200, overflowY: 'auto', marginBottom: 12, border: `1px solid ${C.border}`, borderRadius: 8 }}>
                  {importData.slice(0, 10).map((row, i) => (
                    <div key={i} style={{ padding: '8px 12px', borderBottom: `1px solid ${C.border}`, fontSize: 12, color: C.text }}>
                      {row['Item Name'] || row['item_name']} — Rs.{row['Price'] || row['selling_price']}
                    </div>
                  ))}
                  {importData.length > 10 && <div style={{ padding: '8px 12px', color: C.text3, fontSize: 12 }}>...and {importData.length - 10} more items</div>}
                </div>
                <button onClick={executeImport} disabled={importing}
                  style={{ width: '100%', padding: '12px', background: C.accent, color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer', opacity: importing ? 0.6 : 1 }}>
                  {importing ? 'Importing...' : `Import ${importData.length} Items`}
                </button>
              </div>
            )}
          </div>

          <div style={{ background: C.surface, borderRadius: 12, padding: 24, border: `1px solid ${C.border}` }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: C.text, marginBottom: 8 }}>Excel Format Guide</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: C.bg }}>
                  {['Column', 'Required', 'Example'].map(h => (
                    <th key={h} style={{ padding: '8px 12px', textAlign: 'left', color: C.text3, fontWeight: 700, borderBottom: `1px solid ${C.border}` }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  ['Item Name', 'Yes', 'Dal Makhani'],
                  ['Category', 'No', 'Main Course'],
                  ['Cuisine', 'No', 'Indian'],
                  ['Description', 'No', 'Creamy black lentils'],
                  ['Price', 'Yes', '280'],
                  ['Cost Price', 'No', '80'],
                  ['GST Rate', 'No', '5'],
                  ['Calories', 'No', '180'],
                  ['Veg', 'No', 'Yes / No'],
                  ['Spice Level', 'No', 'Medium'],
                  ['Prep Time', 'No', '20'],
                ].map(([col, req, ex]) => (
                  <tr key={col} style={{ borderBottom: `1px solid ${C.border}` }}>
                    <td style={{ padding: '7px 12px', fontWeight: 600, color: C.text, fontFamily: 'monospace' }}>{col}</td>
                    <td style={{ padding: '7px 12px', color: req === 'Yes' ? C.red : C.text3 }}>{req}</td>
                    <td style={{ padding: '7px 12px', color: C.text2 }}>{ex}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showAdd && <FormModal />}
    </div>
  );
}
